import React from 'react'
import classNames from 'classnames'

export enum Sizes {
  xs = 'xs',
  sm = 'sm',
  base = 'base',
  md = 'md',
  lg = 'lg',
  xl = 'xl',
  '2xl' = '2xl',
  '3xl' = '3xl',
  '4xl' = '4xl',
  '5xl' = '5xl'
}

type Props = {
  isFullPage?: boolean
  isParentSized?: boolean
  /** Shrinks the wrapper to the spinner size, for use next to text (e.g. inside a button) */
  isInline?: boolean
  /** Reserves a viewport-relative min-height so the spinner is vertically centered when it is the whole page/tab content */
  isPageCentered?: boolean
  loading?: boolean
  size?: Sizes
  width?: number
  height?: number
}

// Mapping des tailles vers les classes Tailwind
const sizeClassMap: Record<Sizes, string> = {
  [Sizes.xs]: 'h-4 w-4',
  [Sizes.sm]: 'h-6 w-6',
  [Sizes.base]: 'h-8 w-8',
  [Sizes.md]: 'h-12 w-12',
  [Sizes.lg]: 'h-16 w-16',
  [Sizes.xl]: 'h-20 w-20',
  [Sizes['2xl']]: 'h-24 w-24',
  [Sizes['3xl']]: 'h-32 w-32',
  [Sizes['4xl']]: 'h-40 w-40',
  [Sizes['5xl']]: 'h-48 w-48',
}

// Mapping des épaisseurs de bordure selon la taille
const borderWidthMap: Record<Sizes, string> = {
  [Sizes.xs]: 'border',
  [Sizes.sm]: 'border',
  [Sizes.base]: 'border-2',
  [Sizes.md]: 'border-2',
  [Sizes.lg]: 'border-4',
  [Sizes.xl]: 'border-4',
  [Sizes['2xl']]: 'border-4',
  [Sizes['3xl']]: 'border-8',
  [Sizes['4xl']]: 'border-8',
  [Sizes['5xl']]: 'border-8',
}

const GenericLoading: React.FC<Props> = ({
  isFullPage,
  isParentSized,
  isInline,
  isPageCentered,
  loading = true,
  size = Sizes.md,
  width,
  height,
}) => {
  if (!loading) return null

  // Si width et height sont fournis, on les utilise en priorité
  const customStyle = width && height ? { width: `${width}px`, height: `${height}px` } : {}
  const sizeClass = width && height ? '' : sizeClassMap[size]
  const borderClass = borderWidthMap[size]

  return (
    <div
      className={classNames(
        'flex items-center justify-center pointer-events-auto',
        {
          'fixed inset-0 z-[1000] bg-white/50 backdrop-blur-sm': isFullPage,
          'absolute inset-0 z-50 bg-white/50 backdrop-blur-sm': isParentSized,
          'w-max z-40': !isFullPage && !isParentSized && isInline,
          'w-full h-full z-40': !isFullPage && !isParentSized && !isInline,
          'min-h-[50vh]': isPageCentered,
        }
      )}
    >
      <div
        className={classNames(
          'animate-spin rounded-full border-b-primary mx-auto',
          sizeClass,
          borderClass
        )}
        style={customStyle}
      />
    </div>
  )
}

export { GenericLoading }
export default GenericLoading
